<template>
  <div class="profile-page">
    <div class="profile-shell">
      <header class="profile-head">
        <button type="button" class="head-btn" :aria-label="$t('auth.back')" @click="back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M15 18l-6-6 6-6"></path>
          </svg>
        </button>

        <div class="head-copy">
          <h1>{{ copy.title }}</h1>
          <p>{{ copy.subtitle }}</p>
        </div>

        <button type="button" class="head-btn" :aria-label="$t('auth.home')" @click="goHome">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 11l9-8 9 8"></path>
            <path d="M5 10v10h14V10"></path>
          </svg>
        </button>
      </header>

      <main class="profile-content">
        <section class="form-card">
          <div class="form-field">
            <label>{{ copy.accountLabel }}</label>
            <input class="form-input" :value="accountText" type="text" readonly />
          </div>

          <div class="form-field">
            <label>{{ copy.memberIdLabel }}</label>
            <input class="form-input" :value="memberIdText" type="text" readonly />
          </div>

          <div class="form-field">
            <label>{{ copy.nicknameLabel }}</label>
            <input class="form-input" :value="nicknameText" type="text" readonly />
          </div>

          <div class="form-field">
            <label>{{ copy.realNameLabel }}</label>
            <input
              v-model.trim="realName"
              class="form-input"
              type="text"
              autocomplete="off"
              :placeholder="copy.realNamePlaceholder"
              maxlength="20"
            />
          </div>

          <button
            type="button"
            class="submit-btn"
            :disabled="submitting"
            @click="saveProfile"
          >
            {{ submitting ? copy.savingText : copy.saveText }}
          </button>
        </section>
      </main>
    </div>
  </div>
</template>

<script>
import { getPageCopy } from "@/common/pageLocale";

export default {
  data() {
    return {
      submitting: false,
      userInfo: {},
      realName: "",
    };
  },
  computed: {
    copy() {
      return getPageCopy(this, "infomation");
    },
    accountText() {
      return this.userInfo.username || "--";
    },
    memberIdText() {
      return this.userInfo.id != null && this.userInfo.id !== "" ? String(this.userInfo.id) : "--";
    },
    nicknameText() {
      return this.userInfo.username || "--";
    },
  },
  created() {
    if (!localStorage.getItem("token")) {
      this.$toast(this.copy.loginFirst);
      this.$router.replace("/Login");
      return;
    }
    this.getUserInfo();
  },
  mounted() {
    document.title = `${this.copy.title} · OMN`;
  },
  methods: {
    async getUserInfo() {
      const res = await this.$http({
        method: "get",
        url: "user_info",
      });
      if (res && res.code === 200) {
        this.userInfo = res.data || {};
        this.realName = this.userInfo.name || "";
      } else if (res && res.msg) {
        this.$toast(res.msg);
      }
    },
    back() {
      window.history.back();
    },
    goHome() {
      this.$router.push("/Home");
    },
    async saveProfile() {
      if (!this.realName) {
        this.$toast(this.copy.realNameError);
        return;
      }

      this.submitting = true;
      try {
        const res = await this.$http({
          method: "post",
          url: "user_set_name",
          data: {
            name: this.realName,
          },
        });
        if (res && res.code === 200) {
          this.$toast(res.msg || this.copy.saveSuccess);
          await this.getUserInfo();
          return;
        }
        this.$toast((res && res.msg) || this.copy.realNameError);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.profile-page {
  min-height: 100vh;
  min-height: 100dvh;
  background:
    radial-gradient(circle at 100% 0, rgba(255, 63, 120, 0.11), transparent 28%),
    radial-gradient(circle at 0 14%, rgba(165, 92, 255, 0.1), transparent 26%),
    #0a0710;
  color: #fff7fb;
}

.profile-shell {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  max-width: none;
  margin: 0;
}

.profile-head {
  position: sticky;
  top: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: 2.625rem 1fr 2.625rem;
  align-items: center;
  min-height: 4.1875rem;
  padding: max(0.5rem, env(safe-area-inset-top)) 0.8125rem 0.5rem;
  background: rgba(10, 7, 16, 0.92);
  border-bottom: 1px solid rgba(255, 255, 255, 0.085);
  backdrop-filter: blur(1.125rem);
  -webkit-backdrop-filter: blur(1.125rem);
}

.head-btn {
  width: 2.375rem;
  height: 2.375rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.045);
  color: #fff;
}

.head-btn svg {
  width: 1.1875rem;
  height: 1.1875rem;
}

.head-copy h1 {
  margin: 0;
  text-align: center;
  font-size: 1.125rem;
  font-weight: 900;
  letter-spacing: -0.0125rem;
}

.head-copy p {
  margin: 0.1875rem 0 0;
  text-align: center;
  color: #a89baa;
  font-size: 0.5625rem;
  line-height: 1.4;
}

.profile-content {
  padding: 1rem;
}

.form-card {
  border-radius: 1.375rem;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.085);
  padding: 1rem;
  box-shadow: 0 0.8125rem 2.125rem rgba(0, 0, 0, 0.15);
}

.form-field + .form-field {
  margin-top: 0.875rem;
}

.form-field label {
  display: block;
  margin-bottom: 0.4375rem;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 800;
}

.form-input {
  width: 100%;
  height: 3.125rem;
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.085);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  padding: 0 0.875rem;
  outline: none;
}

.form-input::placeholder {
  color: #776b76;
}

.form-input[readonly] {
  opacity: 0.72;
}

.submit-btn {
  width: 100%;
  height: 3.125rem;
  margin-top: 1.5rem;
  border: 0;
  border-radius: 1.5625rem;
  background: linear-gradient(135deg, #ff6d9c, #ff3f78);
  box-shadow: 0 0.6875rem 1.625rem rgba(255, 63, 120, 0.24);
  color: #fff;
  font-size: 1.0625rem;
  font-weight: 900;
}

.submit-btn[disabled] {
  opacity: 0.6;
}

button {
  cursor: pointer;
}

@media (min-width: 48rem) {
  .profile-shell {
    max-width: 25rem;
    margin: 0 auto;
  }
}
</style>
